

import { queryClient } from "@/lib/api/queryClient";
import type { BuilderSurface } from "@/lib/ai/ai-store";
import { invalidateArrivalNoticeLookups } from "@/lib/query/invalidateArrivalNoticeLookups";
import { invalidateAfterCheckIn } from "@/lib/guests/invalidateAfterCheckIn";
import { reconcileAiCredits } from "@/lib/ai/creditLedger";
import { qk } from "@/lib/query/queryKeys";
import { importsFilterOptionsKey } from "@/hooks/useActivityFilterOptions";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getBackgroundJobStatus,
  getBackgroundJobStatusCompany,
  deleteBackgroundJob,
  deleteBackgroundJobCompany,
  cancelBackgroundJob as cancelBackgroundJobApi,
  cancelBackgroundJobCompany as cancelBackgroundJobCompanyApi,
} from "@/lib/api/handlers/BackgroundJobHandling";
import { BackgroundJob, ImportRowError, JobPortalLinks, JobResultFile, ArrivalNoticeJobOutcome } from "@/types/BackgroundJob";
import { isServerErr, throwServerError } from "@/lib/api/errors/ErrorHandler";
import { armCoarseReconcile } from "@/lib/guest-reconcile/reconcileScheduler";
import { ACCOUNT_TEARDOWN_EVENT } from "@/lib/storage/accountScopedState";
import { AUTH_RECOVERED_EVENT } from "@/lib/auth/AuthContext";

const STORAGE_KEY = "veltric-background-jobs";

// Tolerance for transient backend failures (network blips, brief 401 during
// refresh, etc.). At 1 s poll cadence this is ~5 s of consecutive failure
// before we surface the job as FAILED — long enough to ride out any single
// hiccup, short enough that real worker failures still surface promptly.
const POLL_FAILURE_THRESHOLD = 5;

/**
 * The server no longer has the job: its event was deleted (the event-token
 * filter) or the job itself was purged. Both answer 404 with the code
 * `notFound`. The code is checked too — the cancel route also maps other
 * IllegalStateExceptions to 404, and those are not proof the job is gone.
 */
export function isJobGone(reply: unknown): boolean {
  return isServerErr(reply) && reply.httpStatus === 404 && reply.message === "notFound";
}

// Company-scope task types poll via /background-jobs/company/{companyId}/{jobId}
// rather than the event-shaped route, whose event-token filter finds no event
// for a companyId in the eventId slot (404 notFound).
const COMPANY_SCOPE_TASKS: ReadonlySet<TrackedJobTaskType> = new Set<TrackedJobTaskType>([
  "EXPORT_EVENTS_TO_EXCEL",
  "EXPORT_SPECIFIC_EVENTS_TO_EXCEL",
  "EXPORT_EVENTS_TO_CSV",
  "EXPORT_SPECIFIC_EVENTS_TO_CSV",
  "CONTACT_IMPORT_CREATE",
]);

export function scopeKindFor(taskType: TrackedJobTaskType): "event" | "company" {
  return COMPANY_SCOPE_TASKS.has(taskType) ? "company" : "event";
}

export type TrackedJobTaskType =
  | "SEND_EMAILS"
  | "EXPORT_GUESTS_TO_EXCEL"
  | "EXPORT_SPECIFIC_GUESTS_TO_EXCEL"
  | "EXPORT_GUESTS_TO_CSV"
  | "EXPORT_SPECIFIC_GUESTS_TO_CSV"
  | "EXPORT_EVENTS_TO_EXCEL"
  | "EXPORT_SPECIFIC_EVENTS_TO_EXCEL"
  | "EXPORT_EVENTS_TO_CSV"
  | "EXPORT_SPECIFIC_EVENTS_TO_CSV"
  | "EXPORT_REQUESTS_TO_EXCEL"
  | "EXPORT_REQUESTS_TO_CSV"
  | "EXPORT_CHECK_IN_LOGS_TO_EXCEL"
  | "EXPORT_CHECK_IN_LOGS_TO_CSV"
  | "EXPORT_EMAILS_SENT_TO_EXCEL"
  | "EXPORT_SPECIFIC_EMAILS_SENT_TO_EXCEL"
  | "EXPORT_EMAILS_SENT_TO_CSV"
  | "EXPORT_SPECIFIC_EMAILS_SENT_TO_CSV"
  | "IMPORT_GUESTS_CREATE"
  | "IMPORT_GUESTS_OVERWRITE"
  // Company-scoped CRM contact import (contacts[] on OptionalParams). Lives
  // in COMPANY_SCOPE_TASKS — the tracked job's eventId slot carries the
  // companyId (generic scopeId).
  | "CONTACT_IMPORT_CREATE"
  | "EXPORT_SLOTTING_APPOINTMENTS_PDF"
  | "EXPORT_SLOTTING_REQUESTS_TO_EXCEL"
  | "EXPORT_SLOTTING_REQUESTS_TO_CSV"
  | "EXPORT_SEATING_PDF"
  // A-22 — event configuration as one JSON file (owner/admin; Worker-rendered).
  | "EXPORT_EVENT_CONFIG_TO_JSON"
  // A-22 (6b) — every attachment file as ZIP volumes (owner/admin; Worker-built).
  | "EXPORT_ATTACHMENTS_TO_ZIP"
  | "BULK_UPDATE_GUESTS_BY_FILTER"
  | "BULK_UPDATE_GUESTS_BY_IDS"
  | "BATCH_CHECK_IN_GUESTS"
  // SMS manual send from the guest list (SMS manual send, F-3). Event-scoped,
  // queue-only; one SMS per reachable guest.
  | "SEND_SMS_MANUAL"
  // Server-side config-builder translate (calendar / wallet / portal). Event-
  // scoped — deliberately NOT in COMPANY_SCOPE_TASKS.
  | "TRANSLATE_TEMPLATE"
  // P8 — scheduled-workflow broadcast. Created backend-side by the
  // dispatcher; tracked here only if a surface chooses to poll it.
  | "SCHEDULED_WORKFLOW"
  // Event-goal AI cohort analysis. Event-scoped; the Goals page also polls
  // it locally, this entry only lets the global job tray show it.
  | "ANALYZE_EVENT_GOALS";

export interface TrackedJob extends ArrivalNoticeJobOutcome {
  jobId: string;
  // For event-scope jobs this is the eventId. For company-scope jobs
  // (EXPORT_EVENTS_*) this is the companyId — the BE stores it in the
  // BackgroundJob.eventId field as a generic scopeId. `scopeKind` decides
  // which API route polling/cancel/delete use.
  eventId: string;
  scopeKind?: "event" | "company";
  eventName: string;
  taskType: TrackedJobTaskType;
  totalItems: number;
  /**
   * TRANSLATE_TEMPLATE only: which builder kind and which template the job
   * translates. Carried on `templateTranslationCompleted` so only the builder
   * that has THAT template open reloads (see `isOwnTranslation`). Absent on
   * jobs tracked before 03.11.2026 — those reload every builder of the event.
   */
  kind?: BuilderSurface;
  templateId?: string;
  progress: number;
  // Server-authoritative counters. `serverTotal` is set once by the backend and
  // means "the items this job actually resolved" — messages enqueued post
  // opt-in/quota for a send, ELIGIBLE rows for a batch check-in (NOT the size of
  // the operator's selection); `processedItems` ticks up as they land. Used to
  // render "x of y …". Written by the queue lane and, since 2026-08-26, by the
  // Java executor lanes too (bulk update, batch check-in, in-process import).
  // Undefined until the first poll carries them, and for exports/translate,
  // which have no meaningful item count.
  serverTotal?: number;
  processedItems?: number;
  status: "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";
  // PB-TL-3: how many items the worker could NOT process. A SEND_EMAILS job
  // completes as long as every recipient reached a terminal outcome, so a
  // campaign where half the sends failed is still COMPLETED. Without this the
  // FE had no way to know, and rendered a plain green check over real losses.
  failedCount?: number;
  // SEND_EMAILS: the Worker's two other counters — recipients the fan-out
  // skipped and recipients without an email address. Optional like
  // `failedCount`; the Java poll projection strips them until a later wave.
  skippedCount?: number;
  noEmailCount?: number;
  // The rows an import refused, newest 50, fetched with the terminal payload.
  // `failedCount` alone tells the operator that something was lost but never
  // what — these carry the row number, the offending value and the reason.
  errors?: ImportRowError[];
  // PB-TL-1: the client cannot currently reach the server for this job's
  // status. NOT a job state — the job is still IN_PROGRESS and still polling.
  // Purely a display flag, cleared by the first successful poll. Deliberately
  // not persisted: a stale "unreachable" restored from localStorage on a fresh
  // page load would be a lie until the first poll landed.
  unreachable?: boolean;
  result?: string;
  // Multi-file result (attachment ZIP volumes). Rides the lazy terminal fetch.
  resultFiles?: JobResultFile[];
  // When the job finished (ms), from the same fetch. The presigned links are
  // signed at COMPLETION, so their hour runs from here — for an export that
  // took forty minutes, counting from `createdAt` would hide working links.
  completedAt?: number;
  errorMessage?: string;
  // Portal-link export outcome (`includePortalLink` jobs). `skipped` is the
  // reason the column stayed empty on a COMPLETED job; the panel shows it as
  // a hint line. Rides the same lazy terminal fetch as `failedCount`.
  portalLinks?: JobPortalLinks;
  // SEND_SMS_MANUAL (F-3): why the sender stopped early, the dry-run count and
  // the skips per reason — from the lazy terminal fetch only (the poll stays
  // at its 9 fields), on ANY terminal state.
  smsHaltReason?: string;
  dryrunCount?: number;
  smsSkipCounts?: Record<string, number>;
  createdAt: number;
}

/** The SMS manual send: finished in any terminal state is "done" for its side effects (F3-12). */
const SMS_MANUAL_TASK: TrackedJobTaskType = "SEND_SMS_MANUAL";

/**
 * What `addJob` did. "duplicate" means the jobId was already tracked — a
 * retried submit the backend answered with the ORIGINAL job, or a re-broadcast
 * AI frame — and nothing was added. Callers may ignore it; the send dialog
 * turns it into "this send is already running" (the provider cannot toast:
 * ToastProvider is nested inside it, see src/main.tsx).
 */
export type AddJobResult = "added" | "duplicate";

/**
 * How a cancel ended. "alreadyFinished": the job reached COMPLETED/FAILED
 * before the cancel arrived (409 `jobAlreadyFinished`) — nothing was
 * cancelled, its real status has been read back. "gone": the server no longer
 * has the job (404 — its event was deleted, or the job was purged); the row
 * has left the list. Any other refusal REJECTS with the backend code
 * (throwServerError), so the confirm dialog can say it.
 */
export type CancelJobOutcome = "cancelled" | "alreadyFinished" | "gone";

/**
 * A job the CALLER names for `cancelJob` because this browser may not track
 * it (audit r3 R3-1): the per-send view cancels a manual SMS send that never
 * started from its own card — opened on another device, or after its row was
 * dismissed here. A tracked row with that id always wins.
 */
export type UntrackedJobRef = Pick<TrackedJob, "eventId" | "taskType" | "scopeKind">;

/**
 * The canonical poll projection. Do NOT add fields here casually.
 * BackgroundJobStatusCache.isCanonicalPollProjection requires EXACTLY these 9;
 * a 10th makes the gate return false and every 1s poll bypasses Redis
 * straight to Mongo. PB-TL-3 wanted `failedCount` and takes the lazy
 * terminal-fetch route instead, exactly as `result`/`errorMessage` do.
 */
const POLL_FIELDS =
  "backgroundJobId,eventId,taskType,status,progress,total,processedCount,createdAt,updatedAt";

/** A `smsSkipCounts` reply as a clean map (reason → n > 0), or undefined. */
function smsSkipCountsOf(raw: unknown): Record<string, number> | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const [code, n] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof n === "number" && Number.isFinite(n) && n > 0) out[code] = Math.floor(n);
  }
  return out;
}

/** M-143: the one re-read of a terminal payload that arrived incomplete. */
const LAZY_RETRY_MS = 2000;

/**
 * Task types whose COMPLETED job always carries a `result` (main-backend
 * `completeJob(…, result)` for exports, bulk edits and batch check-ins; the
 * consumer's export lanes the same). A send or an import writes `result` only
 * when it has something to report, so a missing one is normal there. The two
 * OWNER/ADMIN-only results are withheld from everyone else — missing is normal
 * for them too.
 */
function alwaysCarriesResult(taskType: TrackedJobTaskType): boolean {
  if (taskType === "EXPORT_EVENT_CONFIG_TO_JSON" || taskType === "EXPORT_ATTACHMENTS_TO_ZIP") {
    return false;
  }
  return (
    taskType.startsWith("EXPORT") ||
    taskType === "BULK_UPDATE_GUESTS_BY_IDS" ||
    taskType === "BULK_UPDATE_GUESTS_BY_FILTER" ||
    taskType === "BATCH_CHECK_IN_GUESTS"
  );
}

interface BackgroundJobsContextType {
  jobs: TrackedJob[];
  addJob: (params: {
    jobId: string;
    eventId: string;
    eventName: string;
    taskType: TrackedJobTaskType;
    totalItems: number;
    kind?: BuilderSurface;
    templateId?: string;
  }) => AddJobResult;
  /** `local`: forget the row without the server DELETE (its event is gone). */
  dismissJob: (jobId: string, opts?: { local?: boolean }) => void;
  cancelJob: (jobId: string, untracked?: UntrackedJobRef) => Promise<CancelJobOutcome>;
}

const BackgroundJobsContext = createContext<
  BackgroundJobsContextType | undefined
>(undefined);

function loadJobs(): TrackedJob[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const jobs = JSON.parse(raw) as TrackedJob[];
    return jobs.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

function saveJobs(jobs: TrackedJob[]) {
  // PB-TL-1: `unreachable` is connection state, not job state. Persisting it
  // would restore a "can't reach the server" flag on a fresh page load, before
  // a single poll had been attempted — a claim we have no evidence for yet.
  // Stripped on the way out; the first failing poll re-derives it if true.
  const persistable = jobs.map(({ unreachable: _unreachable, ...rest }) => rest);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
}

export const BackgroundJobsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [jobs, setJobs] = useState<TrackedJob[]>([]);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const prevJobStatusRef = useRef<Map<string, string>>(new Map());
  // Per-job consecutive failure counter. In-memory only — never persisted to
  // localStorage. Reset on any successful poll; once it crosses
  // POLL_FAILURE_THRESHOLD, the job is marked FAILED.
  const failureCountsRef = useRef<Map<string, number>>(new Map());
  // Per-job streak of polls answered "the job is gone" (isJobGone). Reset by
  // any other answer; at POLL_FAILURE_THRESHOLD the row is dropped. A streak
  // of answers, not of time — addJob's immediate poll counts too — so it
  // guards against one odd reply, not against a slow insert.
  const goneCountsRef = useRef<Map<string, number>>(new Map());
  // Flips true on `veltric:auth-lost`. While set, polling is paused and any
  // in-flight failures are not counted against the threshold (so jobs aren't
  // nuked just because the user's cookie expired).
  const authLostRef = useRef<boolean>(false);
  // Latest jobs snapshot, read by the interval body so its closure isn't stale.
  // Lets the polling effect depend on `hasInProgress` (a stable boolean) instead
  // of `jobs` itself — without this, the interval would be torn down + recreated
  // every time a progress tick mutates `jobs`, drifting the cadence.
  const jobsRef = useRef<TrackedJob[]>([]);
  // Hash of the last persisted list; "" means "nothing written yet".
  const lastSavedHashRef = useRef<string>("");

  // Load from localStorage on mount
  useEffect(() => {
    setJobs(loadJobs());
  }, []);

  // "Auth recovered" — the signal the pause below always needed. Fired by
  // AuthContext on every markAuthenticated, whether or not an account changed.
  useEffect(() => {
    const onAuthRecovered = () => {
      authLostRef.current = false;
      failureCountsRef.current.clear();
      goneCountsRef.current.clear();
    };
    window.addEventListener(AUTH_RECOVERED_EVENT, onAuthRecovered);
    return () => window.removeEventListener(AUTH_RECOVERED_EVENT, onAuthRecovered);
  }, []);

  // Pause polling when axios fires `veltric:auth-lost` (refresh-token failed,
  // user is being bounced to login). Without this guard, every job's
  // consecutive-failure counter would race past POLL_FAILURE_THRESHOLD before
  // the AuthContext re-renders and unmounts us — flipping a screen full of
  // jobs to FAILED right as the user sees the login redirect.
  //
  // The flag used to be one-way on the assumption that AuthContext unmounts
  // and re-mounts this provider on re-auth. It does NOT: this provider is
  // mounted at the ROOT (src/main.tsx), below AuthProvider but outside
  // PrivateRoute, so it survives every logout — the flag stayed true for the
  // life of the tab and polling never resumed. `veltric:account-teardown` is
  // that missing "auth recovered" signal, and it is also where the previous
  // account's job list — which carries FOREIGN event names and job ids —
  // leaves memory.
  //
  // Resetting the pause on that event is only correct because the teardown
  // fires on an explicit logout or a DIFFERENT account signing in — never on
  // `veltric:auth-lost`. It used to fire there too, which cancelled this very
  // pause in the same tick (child effects register before the parent's, so
  // both handlers run on one dispatch).
  //
  // The teardown alone is NOT enough to un-pause, though: the most common
  // recovery is the SAME user signing back in, which sweeps nothing and fires
  // no teardown. Without the auth-state effect below, one mistyped password
  // (the 401 interceptor has no auth-route exemption) left polling dead for
  // the life of the tab — every later export sat at 0%.
  // M-143: the pending terminal re-reads (one per job, at most) — cleared on
  // teardown and unmount so no read leaves after a logout.
  const lazyRetryTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const lazyRetriedRef = useRef(new Set<string>());
  useEffect(() => {
    const timers = lazyRetryTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);
  useEffect(() => {
    const onAuthLost = () => {
      authLostRef.current = true;
      failureCountsRef.current.clear();
      goneCountsRef.current.clear();
    };
    const onAccountTeardown = () => {
      authLostRef.current = false;
      failureCountsRef.current.clear();
      goneCountsRef.current.clear();
      lazyRetryTimersRef.current.forEach((t) => clearTimeout(t));
      lazyRetryTimersRef.current.clear();
      lazyRetriedRef.current.clear();
      prevJobStatusRef.current.clear();
      jobsRef.current = [];
      // Match the hash the empty list produces, or the persist effect below
      // would write "[]" straight back into the key the teardown just swept.
      lastSavedHashRef.current = "";
      setJobs([]);
    };
    window.addEventListener("veltric:auth-lost", onAuthLost);
    window.addEventListener(ACCOUNT_TEARDOWN_EVENT, onAccountTeardown);
    return () => {
      window.removeEventListener("veltric:auth-lost", onAuthLost);
      window.removeEventListener(ACCOUNT_TEARDOWN_EVENT, onAccountTeardown);
    };
  }, []);

  // PB-TL-4: adopt writes made by OTHER tabs.
  //
  // The store is plain localStorage with no cross-tab sync, so two open tabs
  // diverged and then actively undid each other: tab 1 dismisses A and
  // persists [B]; tab 2 still holds [A,B] in memory, and its next structural
  // save (dismissing B) writes ITS stale array minus B = [A] — resurrecting A
  // and silently reverting tab 1's dismiss. Last writer won with stale data.
  //
  // The `storage` event fires only in OTHER tabs, so this cannot loop back on
  // our own save. We adopt the peer's list wholesale rather than merging:
  // localStorage is the shared source of truth here, and a merge would have to
  // invent a rule for "dismissed in one tab, still present in the other" — the
  // conflict the user already resolved by dismissing.
  //
  // Live progress fields are NOT taken from the peer: each tab polls
  // independently, so a peer's snapshot can be a tick behind. We keep our own
  // progress for jobs we already know about and take only membership from the
  // peer, which is what the dismiss/add conflict is actually about.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      // key removed entirely (storage cleared) → mirror the clear
      if (e.newValue === null) {
        setJobs([]);
        return;
      }
      let peer: TrackedJob[];
      try {
        peer = JSON.parse(e.newValue) as TrackedJob[];
      } catch {
        return; // malformed peer write — keep what we have
      }
      if (!Array.isArray(peer)) return;
      setJobs((mine) => {
        const byId = new Map(mine.map((j) => [j.jobId, j]));
        return peer
          .map((p) => {
            const own = byId.get(p.jobId);
            // Known job: keep OUR live counters (we may be mid-poll), take the
            // peer's row only for jobs we've never seen.
            return own ? { ...p, ...own } : p;
          })
          .sort((a, b) => b.createdAt - a.createdAt);
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Keep the ref in sync so the interval body always reads the freshest jobs.
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Persist to localStorage on change (skip initial empty state).
  // Only writes when a structural field changes (id / status / result / errorMessage /
  // createdAt / total / eventName / taskType). Progress alone does NOT trigger a write —
  // a 180k-recipient send would otherwise sync-stringify the array every poll tick.
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      if (jobs.length > 0) initialized.current = true;
      else if (loadJobs().length === 0) initialized.current = true;
      else return;
    }
    // PB-TL-3: `failedCount` MUST be in this hash.
    //
    // The hash decides whether a save happens at all. The count arrives from
    // the LAZY terminal fetch, which lands AFTER the status transition that
    // triggered the previous save — so with the count absent from the hash,
    // nothing marks the store dirty and the value lives only in memory. The
    // row rendered it correctly and a reload silently lost it, which is a
    // nastier shape than never rendering it: the warning disappears on the
    // next visit while the campaign is still just as broken. Caught by the
    // end-to-end pin (L3c), NOT by the seeded one — L3 writes the count into
    // localStorage itself, so it can never see this.
    const hash = jobs
      .map(
        (j) =>
          // `serverTotal` is in here for the same reason as the fields above it:
          // the cockpit now GATES the count line on it for the executor-lane
          // task types, so a mid-run reload that restored a job without it
          // dropped the number for a poll cycle and showed a tip instead.
          // `portalLinks.skipped` for the same reason as `failedCount`: it
          // arrives on the lazy fetch and must survive a reload.
          // `processedItems` is deliberately NOT here — it changes on nearly
          // every poll and would turn the save-hash into a per-second write.
          // The SMS send's three lazy fields for the same reason as
          // `failedCount` — the halt line must survive a reload.
          `${j.jobId}|${j.status}|${j.result ?? ""}|${j.errorMessage ?? ""}|${j.failedCount ?? ""}|${j.skippedCount ?? ""}|${j.noEmailCount ?? ""}|${j.portalLinks?.skipped ?? ""}|${j.smsHaltReason ?? ""}|${j.dryrunCount ?? ""}|${j.smsSkipCounts ? JSON.stringify(j.smsSkipCounts) : ""}|${j.createdAt}|${j.totalItems}|${j.serverTotal ?? ""}|${j.eventName}|${j.taskType}`,
      )
      .join("§");
    if (hash === lastSavedHashRef.current) return;
    lastSavedHashRef.current = hash;
    saveJobs(jobs);
  }, [jobs]);

  // Dispatch event when an import / bulk-edit job transitions to COMPLETED.
  // Listeners on the guestlist refresh themselves so the user sees the new
  // state without manually reloading.
  useEffect(() => {
    const prev = prevJobStatusRef.current;
    for (const job of jobs) {
      const prevStatus = prev.get(job.jobId);
      // Lazy terminal-payload fetch: the polling projection no longer carries
      // `result` / `errorMessage` (so the Redis status cache can serve normal
      // polls). When a job lands in a terminal state without those fields
      // populated, fetch them once. Fire-and-forget; the merge below skips if
      // the user has already dismissed the job in the meantime.
      // The SMS send also on CANCELLED (F3-12): sent SMS stay billed and the
      // guests marked contacted, so the row must say what went out.
      const isSms = job.taskType === SMS_MANUAL_TASK;
      const wentTerminal =
        prevStatus === "IN_PROGRESS" &&
        (job.status === "COMPLETED" || job.status === "FAILED" || (isSms && job.status === "CANCELLED"));
      const needsResult = job.status === "COMPLETED" && !job.result;
      const needsError = job.status === "FAILED" && !job.errorMessage;
      // PB-TL-3: a COMPLETED job may still have failed items — a SEND_EMAILS
      // job completes once every recipient reached SOME terminal outcome, so a
      // campaign where half the sends failed is COMPLETED with failedCount>0.
      // Fetched HERE rather than in the poll projection: that projection is
      // pinned at exactly 9 fields by the Redis cache gate, and a count only
      // matters once the job is done anyway.
      const needsFailed =
        job.status === "COMPLETED" && job.failedCount === undefined;
      if (wentTerminal && (needsResult || needsError || needsFailed || isSms)) {
        const kind = job.scopeKind ?? scopeKindFor(job.taskType);
        // `errors` rides the same lazy fetch as failedCount, for the same
        // reason: it only matters once the job is done, and the poll
        // projection is pinned at 9 fields by the Redis cache gate. Without
        // it the operator sees a count and nothing else — the rejected rows
        // were written to Mongo and read by no one.
        // `portalLinks` too: a skipped portal-link export is otherwise a
        // green job over an empty column, and only this fetch can say why.
        // `skippedCount`/`noEmailCount` (PB-FB-M13): the send's other two
        // counters — merged below, but a projection only returns what it names.
        // W4 Hop B: an import's arrival-notice outcome (contract-b §10.3),
        // read where the import counters are read.
        // The SMS send's halt and counters ride this fetch too — named only
        // for that task type, so every other job's read stays as it was.
        const LAZY_FIELDS =
          "result,resultFiles,completedAt,errorMessage,failedCount,errors,portalLinks,skippedCount,noEmailCount,arrivalNoticeLinked,arrivalNoticeSkipped,arrivalNoticeWarnings" +
          (isSms ? ",smsHaltReason,smsHaltAt,dryrunCount,smsSkipCounts" : "");
        // M-143: this read is the ONLY read of the finished job's payload, so
        // a failed read used to leave the row without its result or reason
        // for good. Asked ONCE more, 2 s later — when the read failed, when a
        // FAILED job came back without its reason, or when a job that always
        // carries a result came back without one. Never for a missing
        // failedCount: both producers write status + result in one update and
        // the consumer counts failures BEFORE that flip, so absent means zero.
        const lazyFetchOnce = (attempt: number) => {
          const lazyFetch = kind === "company"
            ? getBackgroundJobStatusCompany(job.eventId, job.jobId, LAZY_FIELDS)
            : getBackgroundJobStatus(job.eventId, job.jobId, LAZY_FIELDS);
          const again = () => {
            if (attempt !== 0 || lazyRetriedRef.current.has(job.jobId)) return;
            lazyRetriedRef.current.add(job.jobId);
            const timer = setTimeout(() => {
              lazyRetryTimersRef.current.delete(job.jobId);
              lazyFetchOnce(1);
            }, LAZY_RETRY_MS);
            lazyRetryTimersRef.current.set(job.jobId, timer);
          };
          lazyFetch
            .then((reply) => {
              if ("isServerError" in reply && reply.isServerError) {
                again();
                return;
              }
              const got = reply as BackgroundJob;
              const stillMissing =
                (job.status === "COMPLETED" &&
                  alwaysCarriesResult(job.taskType) &&
                  got.result === undefined &&
                  !(Array.isArray(got.resultFiles) && got.resultFiles.length > 0)) ||
                (job.status === "FAILED" && got.errorMessage === undefined);
              if (stillMissing) again();
              const bgJob = got;
              setJobs((prevJobs) =>
                prevJobs.map((j) =>
                  j.jobId === job.jobId
                    ? {
                        ...j,
                        ...(bgJob.result !== undefined && { result: bgJob.result }),
                        ...(Array.isArray(bgJob.resultFiles) && bgJob.resultFiles.length > 0 && {
                          resultFiles: bgJob.resultFiles,
                        }),
                        ...(bgJob.completedAt && !Number.isNaN(new Date(bgJob.completedAt).getTime()) && {
                          completedAt: new Date(bgJob.completedAt).getTime(),
                        }),
                        ...(bgJob.errorMessage !== undefined && {
                          errorMessage: bgJob.errorMessage,
                        }),
                        ...(bgJob.failedCount != null && {
                          failedCount: bgJob.failedCount,
                        }),
                        // The Worker's other two send counters, whenever the
                        // reply carries them (it does not until the Java
                        // projection admits them — then they land here).
                        ...(bgJob.skippedCount != null && {
                          skippedCount: bgJob.skippedCount,
                        }),
                        ...(bgJob.noEmailCount != null && {
                          noEmailCount: bgJob.noEmailCount,
                        }),
                        ...(bgJob.errors != null && { errors: bgJob.errors }),
                        ...(bgJob.portalLinks != null && {
                          portalLinks: bgJob.portalLinks,
                        }),
                        ...(typeof bgJob.smsHaltReason === "string" && bgJob.smsHaltReason !== "" && {
                          smsHaltReason: bgJob.smsHaltReason,
                        }),
                        ...(typeof bgJob.dryrunCount === "number" && {
                          dryrunCount: bgJob.dryrunCount,
                        }),
                        ...(smsSkipCountsOf(bgJob.smsSkipCounts) && {
                          smsSkipCounts: smsSkipCountsOf(bgJob.smsSkipCounts),
                        }),
                        ...(bgJob.arrivalNoticeLinked != null && {
                          arrivalNoticeLinked: bgJob.arrivalNoticeLinked,
                        }),
                        ...(bgJob.arrivalNoticeSkipped != null && {
                          arrivalNoticeSkipped: bgJob.arrivalNoticeSkipped,
                        }),
                        // Row + reason only: the panel shows nothing else,
                        // and the job list is persisted to localStorage.
                        ...(Array.isArray(bgJob.arrivalNoticeWarnings) && {
                          arrivalNoticeWarnings: bgJob.arrivalNoticeWarnings.map((w) => ({
                            ...(typeof w?.row === "number" && { row: w.row }),
                            ...(typeof w?.reason === "string" && { reason: w.reason }),
                          })),
                        }),
                      }
                    : j,
                ),
              );
              // Audit r1 A5-11: an import that carries an arrival-notice
              // outcome linked guests to contacts (and may have created
              // some) — the guest popup's lookups and the settings list are
              // stale now. Only an event-scoped job has those caches.
              if (
                kind !== "company" &&
                job.eventId &&
                (bgJob.arrivalNoticeLinked != null ||
                  bgJob.arrivalNoticeSkipped != null ||
                  Array.isArray(bgJob.arrivalNoticeWarnings))
              ) {
                void invalidateArrivalNoticeLookups(queryClient, job.eventId);
              }
            })
            .catch(() => again());
        };
        lazyFetchOnce(0);
      }

      // The SMS send: its side effects on ANY finish (F3-12) — a cancelled or
      // failed send may still have sent (billed, guests marked contacted).
      if (isSms && wentTerminal) {
        window.dispatchEvent(
          new CustomEvent("sendSmsCompleted", {
            detail: { eventId: job.eventId },
          }),
        );
        if (job.eventId) {
          // The log (and every per-send view under its prefix), each guest's
          // SMS history, and the allowance on the entitlements read.
          void queryClient.invalidateQueries({ queryKey: qk.events.smsLog(job.eventId) });
          void queryClient.invalidateQueries({ queryKey: ["sentSms", job.eventId] });
          void queryClient.invalidateQueries({ queryKey: qk.events.entitlements(job.eventId) });
        }
        continue;
      }

      if (prevStatus !== "IN_PROGRESS" || job.status !== "COMPLETED") continue;
      if (
        job.taskType === "IMPORT_GUESTS_CREATE" ||
        job.taskType === "IMPORT_GUESTS_OVERWRITE" ||
        // Contact import reuses the same completion event so CRM surfaces can
        // refresh with one listener. detail.eventId carries the job's scopeId
        // — the companyId for this company-scope task.
        job.taskType === "CONTACT_IMPORT_CREATE"
      ) {
        window.dispatchEvent(
          new CustomEvent("importGuestsCompleted", {
            detail: { eventId: job.eventId },
          })
        );
        // The import batch row (id → name + guestCount) is written by the
        // worker, so the id→label catalog behind the "Import" column and its
        // filter picker is a pre-import snapshot the moment the job lands. It
        // lives under its own namespace, outside the ["guestlist", eventId]
        // sweep GuestsProvider runs, and nothing else ever busts it — so the
        // refreshed rows arrived carrying an importId the catalog had never
        // seen and TCCSingleSelect fell through to rendering the raw hex id
        // until a remount re-ran the query. Invalidated on COMPLETED rather
        // than per progress tick purely to avoid a refetch every polling
        // second — a mid-flight read would be corrected by this one anyway,
        // and by the COMPLETED flip every chunk's guestCount $inc is already
        // acknowledged (each worker bumps the batch count before the
        // processedCount that gates completion).
        // Only IMPORT_GUESTS_CREATE actually mints a batch row today. The
        // stamping list itself is much longer than that — see
        // STAMP_OWN_JOB_ID_TASK_TYPES in BackgroundJobService, which now covers
        // fourteen task types — but the extra members carry the job's own id
        // for COUNTERS, never provenance: Guest.importId has exactly one writer
        // in the tree, and the consumer's runOverwrite takes no importId at all. OVERWRITE is kept in this branch as a no-op so the
        // catalog stays correct if that path ever gains provenance;
        // CONTACT_IMPORT_CREATE is excluded outright because its scopeId is a
        // companyId — there is no company-scoped catalog to bust.
        if (job.eventId && job.taskType !== "CONTACT_IMPORT_CREATE") {
          void queryClient.invalidateQueries({
            queryKey: importsFilterOptionsKey(job.eventId),
          });
        }
      } else if (
        job.taskType === "BULK_UPDATE_GUESTS_BY_FILTER" ||
        job.taskType === "BULK_UPDATE_GUESTS_BY_IDS"
      ) {
        window.dispatchEvent(
          new CustomEvent("bulkEditGuestsCompleted", {
            detail: { eventId: job.eventId },
          })
        );
        // The job finishing is not the end of the story: each guest it wrote
        // fires GUEST_UPDATED, and those workflows run afterwards. The refresh
        // above sees the job's own writes but not the automation's — pulse
        // coarsely so the later pass lands too. Never a per-guest wave: a bulk
        // selection can be thousands of rows.
        armCoarseReconcile(job.eventId);
      } else if (job.taskType === "SEND_EMAILS") {
        // Bulk email send job finished — guests opted-in for the send
        // had their `state` flipped UNCONTACTED → CONTACTED by the
        // consumer worker (email-trigger-service post-send hook).
        // Re-fetch the guestlist so the badge picks up the change
        // without a manual reload.
        window.dispatchEvent(
          new CustomEvent("sendEmailsCompleted", {
            detail: { eventId: job.eventId },
          })
        );
        // Sent-mails rows + the email quota banner are cached under their own
        // namespaces; the send landed asynchronously, so mark them stale now.
        // The quota is the EVENT's company's and rides the event's
        // entitlements read (event-scope wave) — the old
        // ["msgMail", eventId, "company"] entry no longer exists, so
        // invalidating it left "mails remaining" on the pre-send figure.
        if (job.eventId) {
          void queryClient.invalidateQueries({ queryKey: ["sentMails", job.eventId] });
          void queryClient.invalidateQueries({ queryKey: qk.events.entitlements(job.eventId) });
        }
      } else if (job.taskType === "BATCH_CHECK_IN_GUESTS") {
        // The event refreshes the check-in log and the check-in table's count.
        // It does NOT reach the guest list — an earlier version of this comment
        // said "the guestlist already listens for" it, and no listener does:
        // `CheckInLogsProvider` takes the log rows, `MetaTableProvider` skips
        // `type === "guest"` outright, `PGuestDetails` re-reads one open pane.
        // This job completes from the app root, where `GuestsProvider` — the
        // only thing that used to invalidate those caches — need not be mounted
        // at all, so the invalidation has to happen here. Same shape as the
        // SEND_EMAILS branch above.
        if (job.eventId) invalidateAfterCheckIn(queryClient, job.eventId);
        window.dispatchEvent(new Event("checkInActionCompleted"));
      } else if (job.taskType === "TRANSLATE_TEMPLATE") {
        // Server-side config-builder translate finished — the persisted config
        // now carries the translated maps + new languages. Tell the open
        // config builder (calendar/wallet/portal) to re-fetch so the editor
        // reloads with the translated content without a manual reload.
        window.dispatchEvent(
          new CustomEvent("templateTranslationCompleted", {
            // kind + templateId: only the builder with THIS template open
            // reloads (isOwnTranslation). Undefined on a pre-03.11. job.
            detail: { eventId: job.eventId, kind: job.kind, templateId: job.templateId },
          }),
        );
        // This is the only job kind that bills Velo AI credits, and the worker
        // refunds whatever it did not run — so the settled number is only
        // knowable here. Without it the ring keeps the pre-translation figure
        // until some later chat turn happens to reconcile.
        reconcileAiCredits();
      }
    }
    prevJobStatusRef.current = new Map(jobs.map((j) => [j.jobId, j.status]));
  }, [jobs]);

  /**
   * Picks the right status-fetch handler for a tracked job's scope.
   * Defaults to event-scope when scopeKind is absent (older localStorage
   * entries persisted before the field existed).
   */
  const fetchStatusFor = useCallback(
    (job: TrackedJob, fields: string) => {
      const kind = job.scopeKind ?? scopeKindFor(job.taskType);
      return kind === "company"
        ? getBackgroundJobStatusCompany(job.eventId, job.jobId, fields)
        : getBackgroundJobStatus(job.eventId, job.jobId, fields);
    },
    [],
  );

  /**
   * Merges a single backend status reply into the existing local job entry.
   * The IN_PROGRESS branch wraps `progress` in `Math.max` so a slow reply
   * landing after a fresh one (e.g. immediate-first-poll racing the regular
   * 1 s tick) can never regress the bar — the worker side guarantees
   * monotonic writes via Mongo `$max`, and we mirror that on the client so
   * partial reorderings on the wire don't undo it.
   *
   * Returns `null` for a transient ServerError reply — the caller treats
   * `null` as "leave the job as IN_PROGRESS this tick" and bumps the
   * per-job consecutive-failure counter. Only after N consecutive failures
   * does the caller flip the job to FAILED.
   */
  const mergeStatusReply = useCallback(
    (job: TrackedJob, result: BackgroundJob | { isServerError: true; message: string }): Partial<TrackedJob> | null => {
      if ("isServerError" in result && result.isServerError) {
        return null;
      }
      const bgJob = result as BackgroundJob;
      const backendCreatedAt = bgJob.createdAt
        ? new Date(bgJob.createdAt).getTime()
        : undefined;
      const carriedCreatedAt =
        backendCreatedAt && !job.createdAt ? { createdAt: backendCreatedAt } : {};

      if (bgJob.status === "COMPLETED") {
        // Only carry `result` if the reply actually has it. The canonical
        // polling projection doesn't include result/errorMessage anymore —
        // the lazy terminal-payload fetch in the status-transition effect
        // is the source of truth for those fields. Spreading
        // `result: undefined` here would overwrite a value the lazy fetch
        // may already have landed.
        // Snap the counter to the total so the row reads "y of y sent" the
        // instant it completes (the last per-recipient tick + the COMPLETE
        // flip can race; without this the count could freeze at e.g. 179/180).
        const completedTotal = bgJob.total ?? job.serverTotal ?? job.totalItems;
        return {
          status: "COMPLETED",
          progress: 100,
          ...(completedTotal !== undefined && {
            serverTotal: completedTotal,
            processedItems: completedTotal,
          }),
          ...(bgJob.result !== undefined && { result: bgJob.result }),
          // Carried only when the reply has them — same rule as `result`.
          ...(bgJob.failedCount != null && { failedCount: bgJob.failedCount }),
          ...(bgJob.skippedCount != null && { skippedCount: bgJob.skippedCount }),
          ...(bgJob.noEmailCount != null && { noEmailCount: bgJob.noEmailCount }),
          ...carriedCreatedAt,
        };
      }
      if (bgJob.status === "FAILED") {
        return {
          status: "FAILED",
          ...(bgJob.errorMessage !== undefined && {
            errorMessage: bgJob.errorMessage,
          }),
          ...carriedCreatedAt,
        };
      }
      if (bgJob.status === "CANCELLED") {
        return { status: "CANCELLED", ...carriedCreatedAt };
      }
      // IN_PROGRESS / PENDING — keep `progress` and the processed counter
      // monotonic (out-of-order replies must never rewind the bar or count).
      return {
        ...(bgJob.progress !== undefined && {
          progress: Math.max(job.progress, bgJob.progress),
        }),
        // `serverTotal` is ratcheted too, not assigned raw. It is the
        // DENOMINATOR of the count line, and `processedItems` right below it is
        // monotonic — so a total that drops while the counter holds its
        // high-water mark renders "460 von 460 Gästen importiert" next to a bar
        // at 12 %, because countProcessed clamps to countTotal. That divergence
        // is reachable whenever a job is re-run on the fallback lane after the
        // queue lane already reported a larger total.
        ...(bgJob.total != null && {
          serverTotal: Math.max(job.serverTotal ?? 0, bgJob.total),
        }),
        ...(bgJob.processedCount != null && {
          processedItems: Math.max(
            job.processedItems ?? 0,
            bgJob.processedCount,
          ),
        }),
        ...carriedCreatedAt,
      };
    },
    [],
  );

  /**
   * Polls every IN_PROGRESS job once. Used by the 1 s interval AND by the
   * immediate-first-poll path in `addJob`. Reads the latest jobs through
   * the ref so the interval-effect's stable dependency (`hasInProgress`)
   * doesn't capture a stale list.
   */
  const pollOnce = useCallback(async () => {
    // While auth is lost (refresh failed), don't poll — every request would
    // 401, blow the consecutive-failure counter, and mark every job FAILED.
    // Polling resumes once AuthContext flips back to authenticated and the
    // user is back on screen.
    if (authLostRef.current) return;
    const active = jobsRef.current.filter((j) => j.status === "IN_PROGRESS");
    if (active.length === 0) return;
    const updates: Record<string, Partial<TrackedJob>> = {};
    const goneIds: string[] = [];
    // Use the canonical 7-field projection so BackgroundJobStatusCache's
    // isCanonicalPollProjection gate matches and Redis serves the poll. The
    // result URL and errorMessage aren't read until status flips to terminal,
    // so they're fetched lazily in `mergeStatusReply`.
    // Do NOT add fields here casually. BackgroundJobStatusCache
    // .isCanonicalPollProjection requires EXACTLY these 9; a 10th makes the
    // gate return false and every 1s poll bypasses Redis straight to Mongo.
    // PB-TL-3 wanted `failedCount` and takes the lazy terminal-fetch route
    // below instead, exactly as `result`/`errorMessage` already do.
    await Promise.all(
      active.map(async (job) => {
        let merged: Partial<TrackedJob> | null = null;
        let gone = false;
        try {
          const result = await fetchStatusFor(job, POLL_FIELDS);
          gone = isJobGone(result);
          merged = mergeStatusReply(job, result);
        } catch {
          merged = null;
        }
        if (gone) {
          // The event was deleted or the job purged. Without this the row
          // polled forever as "can't reach the server": a deleted event's
          // role read fails too, so it showed neither cancel nor dismiss.
          const streak = (goneCountsRef.current.get(job.jobId) ?? 0) + 1;
          goneCountsRef.current.set(job.jobId, streak);
          if (streak >= POLL_FAILURE_THRESHOLD) goneIds.push(job.jobId);
        } else {
          goneCountsRef.current.delete(job.jobId);
        }
        if (merged === null) {
          // Transient failure — bump counter, only mark FAILED after N in a row.
          const next = (failureCountsRef.current.get(job.jobId) ?? 0) + 1;
          failureCountsRef.current.set(job.jobId, next);
          if (next >= POLL_FAILURE_THRESHOLD) {
            // PB-TL-1: do NOT manufacture a terminal FAILED here.
            //
            // This branch used to write `status: "FAILED"`, which is a claim
            // about the JOB made from evidence only about the CONNECTION. Five
            // consecutive 5xx — a redeploy, a network blip, a brief backend
            // outage — took ~5s to invent a failure indistinguishable from a
            // real one. And because FAILED is terminal, the row stopped polling
            // and could never correct itself: the job would run to completion
            // server-side while the user was told it had failed.
            //
            // The job stays IN_PROGRESS and keeps polling; a real terminal
            // state can only come from the server. What the user gets instead
            // is an honest "we can't reach the server" flag on the row, which
            // clears itself on the first successful poll.
            updates[job.jobId] = { unreachable: true };
          }
          return;
        }
        // Success — reset counter and apply. Clearing `unreachable` here is
        // what makes PB-TL-1's flag self-healing: one good poll and the row
        // goes back to normal.
        failureCountsRef.current.delete(job.jobId);
        updates[job.jobId] = job.unreachable ? { ...merged, unreachable: false } : merged;
      }),
    );
    if (goneIds.length > 0) {
      for (const id of goneIds) {
        failureCountsRef.current.delete(id);
        goneCountsRef.current.delete(id);
        delete updates[id];
      }
      setJobs((prev) => prev.filter((j) => !goneIds.includes(j.jobId)));
    }
    if (Object.keys(updates).length === 0) return;
    // Skip the setJobs call when every reply is field-for-field identical to
    // what we already hold. Without this guard, setJobs(prev => prev.map(...))
    // creates a new array reference on every 1s tick even when nothing moved,
    // which churns the context value identity and re-renders every consumer.
    const current = jobsRef.current;
    let anyChanged = false;
    for (const j of current) {
      const u = updates[j.jobId];
      if (!u) continue;
      for (const k of Object.keys(u) as (keyof TrackedJob)[]) {
        if (j[k] !== u[k]) {
          anyChanged = true;
          break;
        }
      }
      if (anyChanged) break;
    }
    if (!anyChanged) return;
    setJobs((prev) =>
      prev.map((j) => (updates[j.jobId] ? { ...j, ...updates[j.jobId] } : j)),
    );
  }, [mergeStatusReply, fetchStatusFor]);

  // Stable boolean — flips only when transitioning between "no active jobs"
  // and "≥1 active job". Decoupling the polling effect from `jobs` itself
  // keeps the interval cadence stable across every progress tick.
  const hasInProgress = useMemo(
    () => jobs.some((j) => j.status === "IN_PROGRESS"),
    [jobs],
  );

  useEffect(() => {
    if (!hasInProgress) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    pollingRef.current = setInterval(() => {
      // Skip the 1s tick while the tab is hidden; it resumes (and catches the
      // final job state) on the next visible tick.
      if (document.hidden) return;
      pollOnce();
    }, 1000);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasInProgress, pollOnce]);

  const addJob = useCallback(
    (params: {
      jobId: string;
      eventId: string;
      eventName: string;
      taskType: TrackedJobTaskType;
      totalItems: number;
      kind?: BuilderSurface;
      templateId?: string;
    }): AddJobResult => {
      // Idempotent on jobId — a double add (e.g. an AI background_job frame
      // re-broadcast, or a retried submit returning the same id) must not
      // create a duplicate row, which would collide React keys + double the
      // progress entry. First add wins — and the caller is TOLD, because a
      // retried submit that the backend answered with the original job (its
      // idempotency key) used to close the dialog green with no word that
      // nothing new was queued. Decided on the ref (the committed list) so the
      // answer is synchronous; the updater below keeps its own guard for the
      // same-tick race.
      if (jobsRef.current.some((j) => j.jobId === params.jobId)) return "duplicate";
      setJobs((prev) => {
        if (prev.some((j) => j.jobId === params.jobId)) return prev;
        return [
          {
            ...params,
            // scopeKind is derived purely from taskType — no caller override.
            // Mismatch would silently 401 (wrong route), so the type system
            // shouldn't even allow it.
            scopeKind: scopeKindFor(params.taskType),
            progress: 0,
            status: "IN_PROGRESS" as const,
            createdAt: Date.now(),
          },
          ...prev,
        ];
      });
      // Fire one immediate poll ~600 ms after submission. 600 ms covers the
      // realistic P99 latency of Java's `setTotal` + Mongo + Redis del before
      // we pull, so the first reply usually carries a non-zero `total`. Even
      // when it doesn't, no regression: the bar stays at 0 and the regular
      // 1 s interval picks up the next tick. The `Math.max` guard in
      // `mergeStatusReply` prevents this immediate poll's reply from clobbering
      // a freshly-arrived interval reply if the network reorders them.
      setTimeout(() => {
        pollOnce();
      }, 600);
      return "added";
    },
    [pollOnce],
  );

  // Read `jobs` through jobsRef so these callbacks stay stable across renders.
  // Without this, deps [jobs] recreated both on every poll tick → context value
  // identity churn → every useBackgroundJobs() consumer re-renders 1×/sec.
  const dismissJob = useCallback((jobId: string, opts?: { local?: boolean }) => {
    const job = jobsRef.current.find((j) => j.jobId === jobId);
    // An SMS send is only ever dismissed HERE (B-1 Delete): its job row holds
    // the skip list the per-send view reads, and the server refuses the DELETE
    // for 7 days anyway.
    if (job && !opts?.local && job.taskType !== SMS_MANUAL_TASK) {
      const kind = job.scopeKind ?? scopeKindFor(job.taskType);
      const del = kind === "company"
        ? deleteBackgroundJobCompany(job.eventId, job.jobId)
        : deleteBackgroundJob(job.eventId, job.jobId);
      del.catch(() => {});
    }
    failureCountsRef.current.delete(jobId);
    goneCountsRef.current.delete(jobId);
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  const cancelJob = useCallback(async (jobId: string, untracked?: UntrackedJobRef): Promise<CancelJobOutcome> => {
    const tracked = jobsRef.current.find((j) => j.jobId === jobId);
    // Not tracked any more (dismissed meanwhile) and not named by the caller:
    // nothing left to cancel here. Named (R3-1): the same call and outcomes;
    // the list below has no row of it to mark, so those updates are no-ops.
    const job = tracked ?? (untracked ? { jobId, ...untracked } : undefined);
    if (!job) return "cancelled";
    const kind = job.scopeKind ?? scopeKindFor(job.taskType);
    const refusal =
      kind === "company"
        ? await cancelBackgroundJobCompanyApi(job.eventId, job.jobId)
        : await cancelBackgroundJobApi(job.eventId, job.jobId);
    // M-12: the handlers RETURN a refusal — it used to be ignored, and the job
    // was marked CANCELLED although the server had refused.
    if (isServerErr(refusal)) {
      if (refusal.message === "jobAlreadyFinished") {
        // The job finished before the cancel arrived: read its real status
        // instead of claiming a cancel (the transition effect then fetches
        // the terminal payload as for any other finish). An untracked job
        // has no row to read it into — its caller reads its own view.
        if (!tracked) return "alreadyFinished";
        try {
          const reply = await fetchStatusFor(tracked, POLL_FIELDS);
          setJobs((prev) =>
            prev.map((j) => {
              if (j.jobId !== jobId) return j;
              const merged = mergeStatusReply(j, reply);
              return merged ? { ...j, ...merged } : j;
            }),
          );
        } catch {
          // The 1 s poll reads it on its next tick anyway.
        }
        return "alreadyFinished";
      }
      if (isJobGone(refusal)) {
        // Rejecting here left an IN_PROGRESS row with no dismiss button that
        // polls a job nobody has — before M-12 the ignored refusal marked it
        // CANCELLED, which was the way out. Drop it locally; there is nothing
        // to DELETE.
        failureCountsRef.current.delete(jobId);
        goneCountsRef.current.delete(jobId);
        setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
        return "gone";
      }
      throwServerError(refusal);
    }
    // PB-TL-2: only a job that is still cancellable may be marked CANCELLED.
    //
    // The confirm dialog opens while the job is IN_PROGRESS, but polling can
    // land COMPLETED before the user confirms. This used to overwrite that
    // fresh COMPLETED state unconditionally — destroying the finished export's
    // `result` download URL — even though the server had done nothing:
    // cancelAndClaimRefund filters `status $nin [COMPLETED,FAILED,CANCELLED]`,
    // so a COMPLETED job matches nothing and the cancel is a 200 NO-OP.
    // Re-read from the CURRENT list inside the updater rather than the `job`
    // captured before the await, which is exactly the value that went stale.
    setJobs((prev) =>
      prev.map((j) => {
        if (j.jobId !== jobId) return j;
        const settled =
          j.status === "COMPLETED" ||
          j.status === "FAILED" ||
          j.status === "CANCELLED";
        return settled ? j : { ...j, status: "CANCELLED" as const };
      })
    );
    return "cancelled";
  }, [fetchStatusFor, mergeStatusReply]);

  const value = useMemo(
    () => ({ jobs, addJob, dismissJob, cancelJob }),
    [jobs, addJob, dismissJob, cancelJob]
  );

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};

export const useBackgroundJobs = () => {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error(
      "useBackgroundJobs must be used within a BackgroundJobsProvider"
    );
  }
  return context;
};
